// @file This store manages screen recording and draft logic
/**
 * This store is used to handle the case where a user is recording their screen for an existing post
 * It saves a copy of the active draft and removes it, then restores it when the screen recording is stopped
 * It also handles the case where a user is recording their screen for a new comment or editing an existing comment
 * - The logic for checking if a comment is being created or edited is from selectFile in SurfaceCommentContentPicker.vue
 * - The logic for drafts is inferred from the chain from selectFile in ContentPicker.vue
 */

import { ContentPickerSource } from '@@/bits/content_picker'
import { __ } from '@@/bits/intl'
import { ScreenRecorderStatus } from '@@/bits/screen_recorder'
import { unboundedWatch } from '@@/bits/vue'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import type { CommentAttachmentState } from '@@/pinia/surface_comment_attachments'
import { useSurfaceCommentAttachmentsStore } from '@@/pinia/surface_comment_attachments'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Cid, CommentId, DraftPost } from '@@/types'
import { useCommentAttachmentUploader } from '@@/vuecomposables/useCommentAttachmentUploader'
import { cloneDeep } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceScreenRecorderStore = defineStore('surfaceScreenRecorderStore', () => {
  // #region visibility for screen recorder modal
  const isVisible = ref(false)
  const showScreenRecorder = (): void => {
    isVisible.value = true
    status.value = ScreenRecorderStatus.Connecting
  }

  const hideScreenRecorder = (): void => {
    isVisible.value = false
    status.value = ScreenRecorderStatus.Hidden
  }
  // #endregion

  // #region status for screen recorder modal
  const status = ref<ScreenRecorderStatus>(ScreenRecorderStatus.Hidden)
  const setStatus = (newStatus: ScreenRecorderStatus): void => {
    status.value = newStatus
  }
  // #endregion

  // #region z index override for screen recorder modal
  const zIndexOverride = ref<number | null>(null)
  const setZIndexOverride = (zIndex: number | null): void => {
    zIndexOverride.value = zIndex
  }
  // #endregion

  // #region draft/comment attachment handling
  // stores used for draft/comment attachment handling
  const surfaceStore = useSurfaceStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const commentsStore = useCommentsStore()
  const surfaceCommentAttachmentStore = useSurfaceCommentAttachmentsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceContentPickerStore = useSurfaceContentPickerStore()
  // Draft states
  const draftCopyForScreenRecording = ref<DraftPost | null>(null)
  const isScreenRecordingForPostDraft = computed(() => draftCopyForScreenRecording.value != null)
  const isScreenRecordingForExistingPost = ref(false)

  // Comment states
  const commentBeingCreatedPostCid = ref<Cid | null>(null)
  const commentBeingEditedId = ref<CommentId | null>(null)
  const draftCommentState = computed<CommentAttachmentState | null>(() => {
    const surfaceCommentAttachmentStore = useSurfaceCommentAttachmentsStore()
    if (commentBeingEditedId.value != null) {
      return commentsStore.draftsForEditedCommentsById[commentBeingEditedId.value]?.attachmentState ?? null
    }
    if (commentBeingCreatedPostCid.value != null) {
      return surfaceCommentAttachmentStore.newCommentAttachmentStateForPostCid(commentBeingCreatedPostCid.value) ?? null
    }
    return null
  })
  const isScreenRecordingForCommentAttachment = ref(false)

  // Overall screen recording state based on draft/ comment attachment states
  const isScreenRecording = computed(
    () => isScreenRecordingForPostDraft.value || isScreenRecordingForCommentAttachment.value,
  )

  /**
   * Save a copy of active draft for screen recording and remove it
   * If there's an active draft:
   * - Save a copy of it
   * - Check if it's for an existing post
   * - Remove the draft
   * If there's an active comment content picker:
   * - Mark as recording for comment attachment
   * - Parse the content picker ID to get post CID and comment ID
   * - Set the appropriate state for new/edited comment
   */
  const saveCopyOfCurrentDraft = (): void => {
    if (surfaceDraftsStore.activeDraftCid != null) {
      draftCopyForScreenRecording.value = cloneDeep(surfaceDraftsStore.activeDraft)
      isScreenRecordingForExistingPost.value = surfacePostsStore.isExistingPost(surfaceDraftsStore.activeDraftCid)
      void surfaceDraftsStore.removeDraft(surfaceDraftsStore.activeDraftCid)
      // hide the post content picker so that we won't have the issues where both
      // the post content picker and the comment content picker would show concurrently
      surfaceContentPickerStore.hidePostContentPicker()
    } else if (surfaceCommentAttachmentStore.activeCommentContentPickerId != null) {
      isScreenRecordingForCommentAttachment.value = true
      const contentPickerId = surfaceCommentAttachmentStore.activeCommentContentPickerId
      const postCid = contentPickerId?.split('p_')?.[1]
      const commentId = contentPickerId?.split('c_')?.[1]

      if (commentId != null) {
        commentBeingEditedId.value = Number(commentId)
      } else if (postCid != null) {
        commentBeingCreatedPostCid.value = postCid
      }
      // hide the comment content picker so that we won't have the issues where both
      // the comment content picker and the post content picker would show concurrently
      surfaceCommentAttachmentStore.hideCommentContentPicker()
    }
  }

  /**
   * Check if a post can be edited during screen recording
   * @param postCid The CID of the post to check
   * @returns true if the post can be edited, false if it's being used for screen recording
   */
  const canEditPostDuringScreenRecording = (postCid: Cid): boolean => {
    if (!isScreenRecordingForExistingPost.value) {
      return true
    }

    if (draftCopyForScreenRecording.value?.cid === postCid) {
      const globalSnackbarStore = useGlobalSnackbarStore()
      globalSnackbarStore.setSnackbar({
        message: __('You are currently editing this post'),
        notificationType: SnackbarNotificationType.error,
      })
      return false
    }

    return true
  }

  /**
   * Get the CID of the post being recorded
   * @returns The CID of the post being recorded, or undefined if no post is being recorded
   */
  const getScreenRecordingPostCid = (): Cid | undefined => {
    return draftCopyForScreenRecording.value?.cid
  }

  /**
   * Restores a draft that was saved for screen recording and uploads the recorded video file
   * If the draft was for an existing post, it will start editing that post.
   * Otherwise it will create a new draft
   * @param file Optional recorded video file to upload
   * @param trimVideoAfterUpload Optional trim settings to apply after upload
   */
  const restorePostDraftAndAddScreenRecording = ({
    file,
    trimVideoAfterUpload,
  }: {
    file: File | null
    trimVideoAfterUpload?: { start: number; end: number }
  }): void => {
    if (draftCopyForScreenRecording.value == null) {
      return
    }

    if (isScreenRecordingForExistingPost.value && draftCopyForScreenRecording.value != null) {
      isScreenRecordingForExistingPost.value = false
      const existingPost = draftCopyForScreenRecording.value
      surfaceDraftsStore.startEditingDraft(existingPost.cid)
      // Remove attachment from existing post if it exists
      // so that the draft post composer will override the existing attachment with the screen recording
      // Use removeDraftAttachment instead of useSurfaceAttachmentPicker().removeAttachment() to avoid showing duplicate "Attachment removed" snackbar
      surfaceDraftsStore.removeDraftAttachment(existingPost.cid)
    } else {
      surfaceDraftsStore.startNewDraft({
        attributes: draftCopyForScreenRecording.value,
        shouldStartEditing: true,
      })
    }
    if (file != null) {
      surfaceDraftsStore.uploadFileActiveDraft({
        file,
        trimVideoAfterUpload,
      })
    }
    draftCopyForScreenRecording.value = null
  }

  /**
   * Restores a draft comment attachment and uploads the recorded video file
   * @param file Optional recorded video file to upload
   * @param trimVideoAfterUpload Optional trim settings to apply after upload
   */
  const restoreCommentAttachmentDraftAndAddScreenRecording = ({
    file,
    trimVideoAfterUpload,
  }: {
    file: File | null
    trimVideoAfterUpload?: { start: number; end: number }
  }): void => {
    if (file == null) {
      return
    }

    if (draftCommentState.value == null) {
      // if user discards the comment that the screen recording was created for,
      // create a new comment then upload file for new comment
      // TODO: Implement this
    }

    const postCid = commentBeingCreatedPostCid.value
    const commentId = commentBeingEditedId.value

    if (commentId != null) {
      useCommentAttachmentUploader().uploadFileForEditedComment({
        commentId,
        file,
        trimVideoAfterUpload,
      })
      commentBeingEditedId.value = null
    } else if (postCid != null) {
      useCommentAttachmentUploader().uploadFileForNewComment({
        postCid,
        file,
        trimVideoAfterUpload,
      })
      commentBeingCreatedPostCid.value = null
    }

    isScreenRecordingForCommentAttachment.value = false
  }

  /**
   * Referenced from WhiteboardAttachmentPicker.vue uploadFileActiveDraft
   * Uploads a file to the whiteboard canvas, handling both modern and legacy upload methods
   * @param payload Object containing file and trim settings
   * @param payload.file The file to upload, or null
   * @param payload.trimVideoAfterUpload Optional settings for trimming video after upload
   * @returns Promise that resolves when the upload is complete
   */
  async function uploadFileActiveDraftForWhiteboard(payload: {
    file: File | null
    trimVideoAfterUpload?: { start: number; end: number }
  }): Promise<void> {
    if (payload.file == null) {
      return
    }
    const { useWhiteboardUploadStore } = await import('@@/pinia/whiteboard_upload')
    const whiteboardUploadStore = useWhiteboardUploadStore()
    const { calculateUploadedFilePositionsAndDimensions } = await import('@@/bits/whiteboard_upload')
    const filePositionsAndDimensions = await calculateUploadedFilePositionsAndDimensions({
      files: [payload.file],
    })
    const { width, height } = filePositionsAndDimensions[0]
    await whiteboardUploadStore.startNewFileUpload(payload.file, {
      contentSource: ContentPickerSource.ScreenshareRecorder,
      trimVideoAfterUpload: payload.trimVideoAfterUpload,
      width,
      height,
      x: CANVAS_WIDTH / 2 - width / 2, // Center the shape
      y: CANVAS_HEIGHT / 2 - height / 2, // Center the shape
    })
  }

  /**
   * Restores the saved draft and uploads the recorded video file based on the current context
   * - For whiteboards: Uploads the file directly to the canvas
   * - For post drafts: Restores the post draft and adds the recording
   * - For comment attachments: Restores the comment draft and adds the recording
   * @param file The recorded video file to upload, or null if no recording
   * @param trimVideoAfterUpload Optional settings for trimming video after upload
   */
  const restoreDraftAndAddScreenRecording = ({
    file,
    trimVideoAfterUpload,
  }: {
    file: File | null
    trimVideoAfterUpload?: { start: number; end: number }
  }): void => {
    if (surfaceStore.isWhiteboard) {
      void uploadFileActiveDraftForWhiteboard({ file, trimVideoAfterUpload })
    }
    if (isScreenRecordingForPostDraft.value) {
      restorePostDraftAndAddScreenRecording({ file, trimVideoAfterUpload })
    } else if (isScreenRecordingForCommentAttachment.value) {
      restoreCommentAttachmentDraftAndAddScreenRecording({ file, trimVideoAfterUpload })
    }
  }

  /**
   * Resets all the screen recording draft state variables back to their initial values
   */
  const resetScreenRecordingDraftState = (): void => {
    draftCopyForScreenRecording.value = null
    commentBeingEditedId.value = null
    commentBeingCreatedPostCid.value = null
    isScreenRecordingForCommentAttachment.value = false
  }
  // #endregion

  const postPropertiesStore = useSurfacePostPropertiesStore()
  void unboundedWatch(isScreenRecording, (newIsScreenRecording) => {
    if (newIsScreenRecording) {
      postPropertiesStore.setWallPostPropertiesScreenRecorderAttachmentSetting(false)
    } else {
      postPropertiesStore.restoreWallPostPropertiesScreenRecorderAttachmentSetting()
    }
  })

  return {
    isVisible,
    status,
    zIndexOverride,
    showScreenRecorder,
    hideScreenRecorder,
    setStatus,
    setZIndexOverride,
    // Draft-related exports
    draftCopyForScreenRecording,
    isScreenRecording,
    isScreenRecordingForExistingPost,
    isScreenRecordingForCommentAttachment,
    commentBeingEditedId,
    commentBeingCreatedPostCid,
    draftCommentState,
    saveCopyOfCurrentDraft,
    canEditPostDuringScreenRecording,
    getScreenRecordingPostCid,
    restoreDraftAndAddScreenRecording,
    resetScreenRecordingDraftState,
  }
})
